import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs';
import { ToastReusable } from '../../../../libs/core';
import { AuthService } from '../../../../libs/core';
import { CrmTokenExpiryService } from './pages/auth/services/crm-token-expiry.service';
import { CrmIdleTimeoutService } from './pages/auth/services/crm-idle-timeout.service';

@Component({
  imports: [RouterModule, ToastReusable],
  selector: 'app-root',
  templateUrl: './app.html',
})
export class App implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly tokenExpiryService = inject(CrmTokenExpiryService);
  private readonly idleTimeoutService = inject(CrmIdleTimeoutService);

  protected title = 'crm';

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.applyTenantPrefixToUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`);

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.applyTenantPrefixToUrl(event.urlAfterRedirects));

    // Start token expiry and idle timeout monitoring
    this.tokenExpiryService.start();
    this.idleTimeoutService.start();
  }

  ngOnDestroy(): void {
    this.tokenExpiryService.stop();
    this.idleTimeoutService.stop();
  }

  private readonly noWhiteLabelPaths = [
    '/login',
    '/verify',
    '/forgot-password',
    '/set-password',
    '/reset-password',
  ];

  private applyTenantPrefixToUrl(routerUrl: string): void {
    const session = this.authService.session();
    if (!session.isAuthenticated) {
      return;
    }

    const tenantCompany = String(session.company || '').trim();
    if (!tenantCompany) {
      return;
    }

    const tenantPrefix = tenantCompany.replace(/\s+/g, '').toLowerCase();
    if (!tenantPrefix) {
      return;
    }

    const normalizedRouterUrl = `/${String(routerUrl || '').replace(/^\/+/, '')}`;
    const normalizedRouterPath = normalizedRouterUrl.split('?')[0].split('#')[0];

    if (this.noWhiteLabelPaths.some((p) => normalizedRouterPath === p || normalizedRouterPath.startsWith(`${p}/`))) {
      return;
    }

    const targetPath = `/${tenantPrefix}${normalizedRouterUrl === '/' ? '' : normalizedRouterUrl}`;

    const currentPath = window.location.pathname;
    const currentFullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const normalizedCurrentPath = `/${String(currentPath || '').replace(/^\/+/, '')}`;
    const currentAlreadyPrefixed =
      normalizedCurrentPath === `/${tenantPrefix}` ||
      normalizedCurrentPath.startsWith(`/${tenantPrefix}/`);

    if (currentAlreadyPrefixed) {
      return;
    }

    if (currentFullPath === targetPath) {
      return;
    }

    window.history.replaceState(window.history.state, '', targetPath);
  }
}
